import React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { Form, Formik } from "formik";

import {
  getAccessSystemSettings,
  resetAccessSystemSettings,
  updateAccessSystemSettings,
} from "app/services/api/accessSystemSettings";
import AccessSystemTicketSettingsTab from "./components/AccessSystemTicketSettingsTab";
import AccessSystemVoucherSettingsTab from "./components/AccessSystemVoucherSettingsTab";
import AccessSystemPdfTicketSettingsTab from "./components/AccessSystemPdfTicketSettingsTab";
import AccessSystemTurnstilesSettingsTab from "./components/AccessSystemTurnstilesSettingsTab";

const toTextarea = (lines) =>
  Array.isArray(lines) ? lines.filter(Boolean).join("\n") : "";

const parseLines = (text) => {
  const raw = String(text ?? "");
  return raw
    .split("\n")
    .map((l) => String(l).trim())
    .filter((l) => l.length > 0);
};

const validateLines = (t, text) => {
  const lines = parseLines(text);
  if (lines.length > 20) {
    return t("pages.accessSystemSettings.validation.maxLines", {
      defaultValue: "Maximálne 20 riadkov.",
    });
  }
  const tooLong = lines.find((l) => l.length > 40);
  if (tooLong) {
    return t("pages.accessSystemSettings.validation.maxLineLength", {
      defaultValue: "Maximálne 40 znakov na riadok.",
    });
  }
  return null;
};

const emptyPdfTicketSettings = () => ({
  thankYouTitle: "",
  thankYouDescription: "",
  infoBlock: "",
  logoUrl: "",
  bottomImageUrl: "",
  footerText: "",
});

const defaultTurnstilesSettings = () => ({
  dlaServerIp: "192.168.1.100",
  mask: "255.255.255.0",
  gateway: "192.168.100.1",
  dns: "8.8.8.8",
  scannerSMode: true,
  scannerInterval: 5,
  scannerDoorReport: false,
});

const getSettingsRoot = (data) => data?.settings ?? data ?? {};

const normalizeFromApi = (data) => {
  const settings = getSettingsRoot(data);
  const ps = settings?.printSettings ?? {};
  const pdf = ps?.pdfTicketSettings ?? {};
  const turnstiles = settings?.turnstilesSettings ?? {};
  const turnstilesDefaults = defaultTurnstilesSettings();
  return {
    settingsBase: settings,
    headerText: toTextarea(ps?.headerLines),
    footerText: toTextarea(ps?.footerLines),
    voucherHeaderText: toTextarea(ps?.headerLinesVoucher),
    voucherFooterText: toTextarea(ps?.footerLinesVoucher),
    pdfTicketSettings: {
      ...emptyPdfTicketSettings(),
      thankYouTitle: String(pdf.thankYouTitle ?? ""),
      thankYouDescription: String(pdf.thankYouDescription ?? ""),
      infoBlock: String(pdf.infoBlock ?? ""),
      logoUrl: String(pdf.logoUrl ?? "").trim(),
      bottomImageUrl: String(pdf.bottomImageUrl ?? "").trim(),
      footerText: String(pdf.footerText ?? ""),
    },
    turnstilesSettings: {
      ...turnstilesDefaults,
      dlaServerIp: String(
        turnstiles.dlaServerIp ?? turnstilesDefaults.dlaServerIp,
      ).trim(),
      mask: String(turnstiles.mask ?? turnstilesDefaults.mask).trim(),
      gateway: String(turnstiles.gateway ?? turnstilesDefaults.gateway).trim(),
      dns: String(turnstiles.dns ?? turnstilesDefaults.dns).trim(),
      scannerSMode:
        turnstiles.scannerSMode != null
          ? Boolean(turnstiles.scannerSMode)
          : turnstilesDefaults.scannerSMode,
      scannerInterval: (() => {
        const v = parseInt(turnstiles.scannerInterval, 10);
        return Number.isFinite(v) && v >= 0 ? v : turnstilesDefaults.scannerInterval;
      })(),
      scannerDoorReport:
        turnstiles.scannerDoorReport != null
          ? Boolean(turnstiles.scannerDoorReport)
          : turnstilesDefaults.scannerDoorReport,
    },
  };
};

const buildPayload = (values) => {
  const settingsBase = values.settingsBase || {};
  const existingPrintSettings = settingsBase.printSettings || {};
  const pdf = values.pdfTicketSettings || emptyPdfTicketSettings();
  const turnstiles = values.turnstilesSettings || defaultTurnstilesSettings();
  return {
    ...settingsBase,
    printSettings: {
      ...existingPrintSettings,
      headerLines: parseLines(values.headerText),
      footerLines: parseLines(values.footerText),
      headerLinesVoucher: parseLines(values.voucherHeaderText),
      footerLinesVoucher: parseLines(values.voucherFooterText),
      pdfTicketSettings: {
        ...(existingPrintSettings.pdfTicketSettings || {}),
        thankYouTitle: String(pdf.thankYouTitle ?? ""),
        thankYouDescription: String(pdf.thankYouDescription ?? ""),
        infoBlock: String(pdf.infoBlock ?? ""),
        logoUrl: String(pdf.logoUrl ?? "").trim(),
        bottomImageUrl: String(pdf.bottomImageUrl ?? "").trim(),
        footerText: String(pdf.footerText ?? ""),
      },
    },
    turnstilesSettings: {
      ...(settingsBase.turnstilesSettings || {}),
      dlaServerIp: String(turnstiles.dlaServerIp ?? "").trim(),
      mask: String(turnstiles.mask ?? "").trim(),
      gateway: String(turnstiles.gateway ?? "").trim(),
      dns: String(turnstiles.dns ?? "").trim(),
      scannerSMode: turnstiles.scannerSMode === true,
      scannerInterval: (() => {
        const v = parseInt(turnstiles.scannerInterval, 10);
        return Number.isFinite(v) && v >= 0 ? v : 5;
      })(),
      scannerDoorReport: turnstiles.scannerDoorReport === true,
    },
  };
};

const isValidIpv4 = (value) => {
  const parts = String(value ?? "").trim().split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    if (part.length > 1 && part.startsWith("0")) return false;
    const number = Number(part);
    return Number.isInteger(number) && number >= 0 && number <= 255;
  });
};

const validateTurnstilesSettings = (t, values) => {
  const errors = {};
  const requiredMessage = t("pages.accessSystemSettings.validation.required", {
    defaultValue: "Povinné pole.",
  });
  const invalidIpMessage = t(
    "pages.accessSystemSettings.validation.invalidIpv4",
    {
      defaultValue: "Zadajte platnú IPv4 adresu.",
    },
  );

  ["dlaServerIp", "mask", "gateway", "dns"].forEach((field) => {
    const value = String(values?.[field] ?? "").trim();
    if (!value) {
      errors[field] = requiredMessage;
    } else if (!isValidIpv4(value)) {
      errors[field] = invalidIpMessage;
    }
  });

  return errors;
};

const AccessSystemSettingsPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  const [settingsTab, setSettingsTab] = React.useState("tickets");
  const [infoBlockEditorKey, setInfoBlockEditorKey] = React.useState(0);
  const [initialValues, setInitialValues] = React.useState({
    settingsBase: {},
    headerText: "",
    footerText: "",
    voucherHeaderText: "",
    voucherFooterText: "",
    pdfTicketSettings: emptyPdfTicketSettings(),
    turnstilesSettings: defaultTurnstilesSettings(),
  });

  const title = t("pages.accessSystemSettings.title", {
    defaultValue: "Nastavenia prístupového systému",
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getAccessSystemSettings();
      setInitialValues(normalizeFromApi(data));
    } catch (e) {
      setLoadError(e);
      enqueueSnackbar(
        t("pages.accessSystemSettings.notifications.loadFailed", {
          defaultValue: "Nepodarilo sa načítať nastavenia prístupového systému.",
        }),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleReset = React.useCallback(() => {
    if (resetting) return;
    showDialog({
      variant: "confirm",
      title: t("pages.accessSystemSettings.resetConfirm.title", {
        defaultValue: "Reset na default",
      }),
      content: (
        <Typography variant="body2">
          {t("pages.accessSystemSettings.resetConfirm.content", {
            defaultValue: "Naozaj chcete resetovať nastavenia na default hodnoty?",
          })}
        </Typography>
      ),
      onYes: async () => {
        hideDialog();
        setResetting(true);
        try {
          const data = await resetAccessSystemSettings();
          setInitialValues(normalizeFromApi(data));
          setInfoBlockEditorKey((k) => k + 1);
          enqueueSnackbar(
            t("pages.accessSystemSettings.notifications.resetSuccess", {
              defaultValue: "Nastavenia boli resetované na default.",
            }),
            { variant: "success" },
          );
        } catch (e) {
          enqueueSnackbar(
            t("pages.accessSystemSettings.notifications.resetFailed", {
              defaultValue: "Reset nastavení sa nepodaril.",
            }),
            { variant: "error" },
          );
        } finally {
          setResetting(false);
        }
      },
      onNo: () => {},
    });
  }, [enqueueSnackbar, hideDialog, resetting, showDialog, t]);

  const handleTabChange = React.useCallback((_, v) => {
    setSettingsTab(v);
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, width: "100%", maxWidth: "none" }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link to="/">{t("sidebar.menuItem.home")}</Link>
          <Link to="/customer/dsok/access-system/tickets-management">
            {t("sidebar.menuItem.accessSystem")}
          </Link>
          <Typography color="text.primary">{title}</Typography>
        </Breadcrumbs>
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {t("pages.accessSystemSettings.loading", {
              defaultValue: "Načítavam...",
            })}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "none" }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/access-system/tickets-management">
          {t("sidebar.menuItem.accessSystem")}
        </Link>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ mb: 2 }}>
        {title}
      </Typography>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("pages.accessSystemSettings.loadError", {
            defaultValue: "Nepodarilo sa načítať nastavenia.",
          })}
        </Alert>
      ) : null}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validate={(values) => {
          const errors = {};

          const headerErr = validateLines(t, values.headerText);
          if (headerErr) errors.headerText = headerErr;
          const footerErr = validateLines(t, values.footerText);
          if (footerErr) errors.footerText = footerErr;

          const vHeaderErr = validateLines(t, values.voucherHeaderText);
          if (vHeaderErr) errors.voucherHeaderText = vHeaderErr;
          const vFooterErr = validateLines(t, values.voucherFooterText);
          if (vFooterErr) errors.voucherFooterText = vFooterErr;

          const turnstilesErr = validateTurnstilesSettings(
            t,
            values.turnstilesSettings,
          );
          if (Object.keys(turnstilesErr).length > 0) {
            errors.turnstilesSettings = turnstilesErr;
          }

          return errors;
        }}
        onSubmit={async (values, { setSubmitting, setValues }) => {
          if (saving) return;
          setSubmitting(true);
          setSaving(true);
          try {
            const payload = buildPayload(values);
            const data = await updateAccessSystemSettings(payload);
            const next = normalizeFromApi(data);
            setValues(next, false);
            setInfoBlockEditorKey((k) => k + 1); // remounts PDF ticket CKEditors (info block + thank-you HTML)
            enqueueSnackbar(
              t("pages.accessSystemSettings.notifications.saveSuccess", {
                defaultValue: "Nastavenia boli uložené.",
              }),
              { variant: "success" },
            );
          } catch (e) {
            enqueueSnackbar(
              t("pages.accessSystemSettings.notifications.saveFailed", {
                defaultValue: "Uloženie nastavení sa nepodarilo.",
              }),
              { variant: "error" },
            );
          } finally {
            setSaving(false);
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          setFieldValue,
          handleSubmit,
          handleBlur,
          setFieldTouched,
        }) => {
          const linesHelp = t("pages.accessSystemSettings.printSettings.linesHelp", {
            defaultValue:
              "Jeden riadok = jeden riadok na tlači. Maximálne 40 znakov na riadok.",
          });

          const disableSave = saving || resetting;
          const disableReset = resetting || saving;

          return (
            <Form onSubmit={handleSubmit} noValidate>
              <TabContext value={settingsTab}>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                  <TabList onChange={handleTabChange}>
                    <Tab
                      label={t("pages.accessSystemSettings.tabs.tickets", {
                        defaultValue: "Nastavenie lístkov",
                      })}
                      value="tickets"
                    />
                    <Tab
                      label={t("pages.accessSystemSettings.tabs.vouchers", {
                        defaultValue: "Nastavenie poukážok",
                      })}
                      value="vouchers"
                    />
                    <Tab
                      label={t("pages.accessSystemSettings.tabs.pdfTicket", {
                        defaultValue: "Nastavenie PDF listka",
                      })}
                      value="pdfTicket"
                    />
                    <Tab
                      label={t("pages.accessSystemSettings.tabs.turnstiles", {
                        defaultValue: "Turnikety",
                      })}
                      value="turnstiles"
                    />
                  </TabList>
                </Box>

                <TabPanel value="tickets" sx={{ px: 0, pt: 0 }}>
                  <AccessSystemTicketSettingsTab
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    linesHelp={linesHelp}
                    t={t}
                  />
                </TabPanel>

                <TabPanel value="vouchers" sx={{ px: 0, pt: 0 }}>
                  <AccessSystemVoucherSettingsTab
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    linesHelp={linesHelp}
                    t={t}
                  />
                </TabPanel>

                <TabPanel value="pdfTicket" sx={{ px: 0, pt: 0 }}>
                  <AccessSystemPdfTicketSettingsTab
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    handleBlur={handleBlur}
                    infoBlockEditorKey={infoBlockEditorKey}
                    thankYouDescriptionEditorKey={infoBlockEditorKey}
                    t={t}
                  />
                </TabPanel>

                <TabPanel value="turnstiles" sx={{ px: 0, pt: 0 }}>
                  <AccessSystemTurnstilesSettingsTab
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    t={t}
                  />
                </TabPanel>
              </TabContext>

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleReset}
                  disabled={disableReset}
                >
                  {t("pages.accessSystemSettings.resetButton", {
                    defaultValue: "Reset na default",
                  })}
                </Button>
                <Button type="submit" variant="contained" disabled={disableSave}>
                  {t("buttons.save")}
                </Button>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default AccessSystemSettingsPage;
